import { useEffect, useMemo, useState } from 'react';
import Card from '../ui/Card';
import { useAuth } from '../../contexts/AuthContext';
import { useAcademySettings, DEFAULT_ACADEMY_SETTINGS } from '../../hooks/useAcademySettings';
import { useBranches } from '../../hooks/useBranches';
import { saveAcademyProfile, uploadAcademyLogo } from '../../utils/settingsMutations';

const AcademySettings = () => {
  const { currentUser } = useAuth();
  const { academy, loading } = useAcademySettings();
  const { activeList } = useBranches();

  const [values, setValues] = useState(DEFAULT_ACADEMY_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (loading) return;
    setValues({
      ...DEFAULT_ACADEMY_SETTINGS,
      ...academy,
      tvecRegistrations: academy.tvecRegistrations || {},
    });
  }, [loading, academy]);

  const flash = (type, text) => {
    setMessage({ type, text });
    window.setTimeout(() => setMessage(null), 4000);
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleTvecChange = (branchId) => (event) => {
    const value = event.target.value;
    setValues((prev) => ({
      ...prev,
      tvecRegistrations: { ...(prev.tvecRegistrations || {}), [branchId]: value },
    }));
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadAcademyLogo({ file });
      setValues((prev) => ({ ...prev, logoURL: result.logoURL }));
      flash('success', 'Logo uploaded. Remember to save.');
    } catch (err) {
      flash('error', err?.message || 'Logo upload failed.');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await saveAcademyProfile({
        profile: {
          ...values,
          tvecRegistrations: values.tvecRegistrations || {},
        },
        operator: currentUser,
      });
      flash('success', 'Academy profile saved.');
    } catch (err) {
      flash('error', err?.message || 'Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };

  const tvecEntries = useMemo(() => {
    return activeList.map((branch) => ({
      branch,
      value: values.tvecRegistrations?.[branch.id] || '',
    }));
  }, [activeList, values.tvecRegistrations]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Card className="shadow-sm">
        <h2 className="text-lg font-semibold text-stone-900">Academy profile</h2>
        <p className="text-sm text-stone-500">
          This information appears on ID cards, sidebar, kiosk and welcome emails.
        </p>

        {message ? (
          <div
            className={`mt-4 rounded-md px-3 py-2 text-sm ${
              message.type === 'success'
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                : 'bg-red-50 text-red-700 ring-1 ring-red-200'
            }`}
          >
            {message.text}
          </div>
        ) : null}

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Academy name
            </span>
            <input
              type="text"
              value={values.name}
              onChange={handleChange('name')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Short name
            </span>
            <input
              type="text"
              value={values.shortName}
              onChange={handleChange('shortName')}
              placeholder="e.g. Brissbella"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Registration number
            </span>
            <input
              type="text"
              value={values.registrationNumber}
              onChange={handleChange('registrationNumber')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label className="sm:col-span-2">
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Tagline
            </span>
            <input
              type="text"
              value={values.tagline}
              onChange={handleChange('tagline')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Contact email
            </span>
            <input
              type="email"
              value={values.contactEmail}
              onChange={handleChange('contactEmail')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Contact phone
            </span>
            <input
              type="tel"
              value={values.contactPhone}
              onChange={handleChange('contactPhone')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Website
            </span>
            <input
              type="url"
              value={values.website}
              onChange={handleChange('website')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
          <label>
            <span className="mb-1 block text-sm font-medium text-stone-700">
              Address
            </span>
            <input
              type="text"
              value={values.addressLine}
              onChange={handleChange('addressLine')}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
            />
          </label>
        </div>
      </Card>

      <Card className="shadow-sm">
        <h3 className="text-base font-semibold text-stone-900">Logo</h3>
        <p className="text-sm text-stone-500">
          Square PNG/SVG works best. Shown in the sidebar and on student ID cards.
        </p>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-xl border border-stone-200 bg-white">
            {values.logoURL ? (
              <img
                src={values.logoURL}
                alt="Academy logo"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="px-2 text-center text-xs text-stone-400">
                No logo uploaded
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-stone-300 bg-white px-3 py-2 text-sm font-medium text-stone-700 shadow-sm hover:bg-stone-50">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploading}
                className="hidden"
              />
              {uploading ? 'Uploading...' : 'Upload new logo'}
            </label>
            {values.logoURL ? (
              <button
                type="button"
                onClick={() =>
                  setValues((prev) => ({ ...prev, logoURL: '' }))
                }
                className="text-left text-xs font-medium text-red-600 hover:text-red-700"
              >
                Remove logo (save to apply)
              </button>
            ) : null}
          </div>
        </div>
      </Card>

      <Card className="shadow-sm">
        <h3 className="text-base font-semibold text-stone-900">
          TVEC registration numbers
        </h3>
        <p className="text-sm text-stone-500">
          Add the TVEC registration number issued for each branch. Used on printed
          certificates and ID cards.
        </p>

        <div className="mt-4 space-y-3">
          {tvecEntries.length === 0 ? (
            <p className="text-sm text-stone-500">
              Add active branches first to capture per-branch TVEC numbers.
            </p>
          ) : (
            tvecEntries.map(({ branch, value }) => (
              <label key={branch.id} className="block">
                <span className="mb-1 block text-sm font-medium text-stone-700">
                  {branch.name}
                </span>
                <input
                  type="text"
                  value={value}
                  onChange={handleTvecChange(branch.id)}
                  placeholder="e.g. TVEC/XXXX"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-pink-500 focus:ring-pink-500"
                />
              </label>
            ))
          )}
        </div>
      </Card>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-pink-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-pink-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving...' : 'Save academy profile'}
        </button>
      </div>
    </form>
  );
};

export default AcademySettings;
